export type AdSurface =
  | 'content-detail-banner'
  | 'mobile-content-detail-banner'
  | 'recommend-column'
  | 'sticky-bottom-banner'
  | 'full3d-mobile-sticky-banner'
  | 'vast-overlay'

export const AD_TELEMETRY_EVENTS = [
  'ad_request_sent',
  'ad_response_received',
  'ad_impression',
] as const

export type AdTelemetryEventName = (typeof AD_TELEMETRY_EVENTS)[number]

export type AdTelemetryConfig = Record<AdTelemetryEventName, boolean>

export type AdScreenOrientation = 'portrait' | 'landscape'

/** Viewing conditions measured when a GPT request or impression event is emitted. */
export type AdViewContextProperties = {
  /** Reported on refresh requests and impressions only, and omitted while the observation is not available yet. */
  is_in_viewport?: boolean
  is_page_visible: boolean
  screen_orientation: AdScreenOrientation
}

export type AdRequestSentProperties =
  | ({
      ad_surface: Exclude<AdSurface, 'vast-overlay'>
      ad_request_id: string
      ad_unit_path: string
      is_refresh: boolean
      refresh_sequence: number
    } & AdViewContextProperties)
  | {
      ad_surface: 'vast-overlay'
      ad_request_id: string
      has_vast_tag_url: boolean
      is_sdk_ready_at_request: boolean
    }

export type AdResponseReceivedProperties =
  | {
      ad_surface: Exclude<AdSurface, 'vast-overlay'>
      ad_request_id: string
      ad_unit_path: string
      is_empty: boolean
      is_blocked: boolean
      response_time_ms: number
      request_attempt_count: number
      line_item_id: number | null
      creative_id: number | null
      size: string | null
      is_refresh: boolean
      refresh_sequence: number
    }
  | {
      ad_surface: 'vast-overlay'
      ad_request_id: string
      is_empty: boolean
      is_blocked: boolean
      response_time_ms: number
      failure_stage: VastFailureStage
      ima_error_code: number | null
      ima_vast_error_code: number | null
      ima_error_type: string | null
      ima_error_message: string | null
    }

export type AdImpressionProperties =
  | ({
      ad_surface: Exclude<AdSurface, 'vast-overlay'>
      ad_request_id: string
      ad_unit_path: string
      time_to_impression_ms: number
      is_refresh: boolean
      refresh_sequence: number
    } & AdViewContextProperties)
  | {
      ad_surface: 'vast-overlay'
      ad_request_id: string
      time_to_impression_ms: number
    }

export type VastFailureStage =
  | 'none'
  | 'sdk_script'
  | 'sdk_timeout'
  | 'ads_loader'
  | 'ads_manager'
  | 'manager_start'
  | 'manager_event_timeout'
